import React from 'react';
import {View, Text, StyleSheet, TouchableOpacity} from 'react-native';
import {Image} from 'expo-image';
import {Video} from 'lucide-react-native';
import {useTranslation} from 'react-i18next';
import {AppDataMediaItem} from "@/data/app-data-interfaces";
import {useCurrentTheme} from "@/theming/useCurrentTheme";
import {ThemeType} from "@/theming/baseTheme";
import {FolderBreadcrumb} from "@/components/media/FolderBreadcrumb";
import {PlayButton} from "@/components/common/PlayButton";
import {getVideoUrl} from "@/data/app-data-api";
import {formatDuration} from "@/lib/utils";
import {OpenButton} from "@/components/common/OpenButton";

interface RelatedVideosProps {
    videos: AppDataMediaItem[];
    orgId: string;
    onVideoSelect: (videoId: string) => void;
}

export const RelatedVideos: React.FC<RelatedVideosProps> = ({
                                                                videos,
                                                                orgId,
                                                                onVideoSelect
                                                            }) => {
    const {t} = useTranslation();
    const {theme} = useCurrentTheme();
    const styles = createStyles(theme);

    // Don't render if no videos
    if (videos.length === 0) {
        return null;
    }

    return (
        <View style={styles.relatedSection}>
            <View style={styles.relatedHeader}>
                <View style={styles.relatedHeaderIcon}>
                    <Video size={18} color={theme.colors.text} strokeWidth={2}/>
                </View>
                <Text style={styles.relatedTitle}>{t('media.relatedVideos')}</Text>
            </View>

            <View style={styles.relatedVideos}>
                {videos.map((video) => (
                    <TouchableOpacity
                        key={video.id}
                        style={styles.relatedVideo}
                        onPress={() => onVideoSelect(video.id)}
                    >
                        <View style={styles.relatedThumbnailContainer}>
                            <Image
                                source={video.image.sm}
                                style={styles.relatedThumbnail}
                                placeholder={{blurhash: video.imageBlurhash}}
                                contentFit="cover"
                                transition={200}
                            />
                            <View style={styles.relatedPlayIconContainer}>
                                {(video.type === 'video' || video.type === 'audio') ? (
                                    <PlayButton size={24} onPress={() => onVideoSelect(video.id)} />
                                ) : (
                                    <OpenButton size={24} onPress={() => onVideoSelect(video.id)} />
                                )}
                            </View>
                        </View>
                        <View style={styles.relatedInfo}>
                            <Text style={styles.relatedVideoTitle} numberOfLines={2}>
                                {video.name}
                            </Text>
                            <View style={styles.relatedVideoMeta}>
                                {video.duration > 0 && <><Text style={styles.relatedMetaText}>{formatDuration(video.duration)}</Text>
                                <Text style={styles.relatedMetaText}>•</Text></>}
                                <FolderBreadcrumb folderId={video.folderId} orgId={orgId} size={'sm'}/>
                            </View>
                        </View>
                    </TouchableOpacity>
                ))}
            </View>
        </View>
    );
};

const createStyles = (theme: ThemeType) => StyleSheet.create({
    relatedSection: {
        backgroundColor: theme.colors.surface+'50',
        borderRadius: 16,
        padding: 20,
        borderWidth: 1,
        borderColor: theme.colors.border,
    },
    relatedHeader: {
        flexDirection: 'row',
        alignItems: 'center',
        marginBottom: 16,
        gap: 8,
    },
    relatedHeaderIcon: {
        alignItems: 'center',
        justifyContent: 'center',
    },
    relatedTitle: {
        fontSize: 18,
        fontWeight: '700',
        color: theme.colors.text,
    },
    relatedVideos: {
        gap: 12,
    },
    relatedVideo: {
        flexDirection: 'row',
        padding: 12,
        backgroundColor: theme.colors.surface,
        borderRadius: 8,
        borderWidth: 1,
        borderColor: theme.colors.border,
    },
    relatedThumbnailContainer: {
        position: 'relative',
        marginRight: 12,
    },
    relatedThumbnail: {
        width: 80,
        height: 45,
        borderRadius: 6,
    },
    relatedPlayIconContainer: {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        justifyContent: 'center',
        alignItems: 'center',
    },
    relatedInfo: {
        flex: 1,
    },
    relatedVideoTitle: {
        fontSize: 14,
        fontWeight: '600',
        color: theme.colors.text,
        marginBottom: 4,
    },
    relatedVideoMeta: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
    },
    relatedMetaText: {
        fontSize: 12,
        color: theme.colors.textSecondary,
    },
});
